import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, formatApiError, fmtNum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import {
    Plus, Star, Truck, Award, Clock, ShoppingCart, TrendingDown, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";

export default function QuotationComparison() {
    const { user } = useAuth();
    const canAddQuo = can(user, "quotation.create");
    const canCreatePo = can(user, "po.create");
    const { id } = useParams(); // rfq id
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [poOpen, setPoOpen] = useState(false);
    const [selectedQuo, setSelectedQuo] = useState(null);
    const [projects, setProjects] = useState([]);

    const load = () => Promise.all([
        api.get(`/rfqs/${id}/comparison`),
        api.get("/products"),
        api.get("/projects"),
    ]).then(([d, p, prj]) => { setData(d.data); setProducts(p.data); setProjects(prj.data); setLoading(false); });

    useEffect(() => { load(); }, [id]);

    if (loading || !data) {
        return <AppShell title="Teklif Karşılaştırma"><div className="py-20 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2"/>Yükleniyor...</div></AppShell>;
    }

    const { rfq, pr, matrix, suppliers } = data;

    const createPO = async (formData) => {
        try {
            const res = await api.post(`/purchase-orders/from-quotation/${selectedQuo.quotation_id}`, formData);
            toast.success(`Sipariş ${res.data.number} oluşturuldu`);
            setPoOpen(false);
            nav(`/purchase-orders`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell
            title={`Teklif Karşılaştırma - ${rfq?.number}`}
            subtitle="RFQ"
            actions={
                canAddQuo ? (
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-quo-btn" className="gap-2"><Plus size={16}/> Teklif Ekle</Button>
                    </DialogTrigger>
                    <QuotationForm rfq={rfq} pr={pr} onClose={()=>setAddOpen(false)} onSaved={load}/>
                </Dialog>
                ) : null
            }
        >
            {/* Summary strip */}
            <div className="mb-4 flex items-center gap-4 text-sm text-muted-foreground">
                <div>Talep: <span className="font-mono text-foreground">{pr?.number}</span></div>
                <div>Kalem: <span className="text-foreground">{matrix.length}</span></div>
                <div>Alınan Teklif: <span className="text-foreground">{suppliers.length}</span></div>
            </div>

            {suppliers.length === 0 ? (
                <div className="bg-card border border-border rounded-md p-16 text-center">
                    <div className="text-muted-foreground mb-4">Henüz teklif eklenmedi.</div>
                    <Button onClick={()=>setAddOpen(true)} data-testid="empty-add-quo">
                        <Plus size={16} className="mr-2"/> İlk Teklifi Ekle
                    </Button>
                </div>
            ) : (
                <>
                    {/* Best value strip */}
                    <div className="grid md:grid-cols-4 gap-3 mb-4">
                        <BestCard icon={TrendingDown} label="En Düşük Toplam"
                                  value={fmtMoney(Math.min(...suppliers.filter(s=>s.total_amount_base>0).map(s=>s.total_amount_base)), "TRY")}
                                  sup={suppliers.find(s=>s.is_lowest_total)?.supplier_name}/>
                        <BestCard icon={Clock} label="En Hızlı Teslim"
                                  value={`${Math.min(...suppliers.filter(s=>s.delivery_days>0).map(s=>s.delivery_days))} gün`}
                                  sup={suppliers.find(s=>s.is_fastest)?.supplier_name}/>
                        <BestCard icon={Truck} label="En İyi Vade"
                                  value={`${Math.max(...suppliers.map(s=>s.payment_term_days))} gün`}
                                  sup={suppliers.find(s=>s.is_best_terms)?.supplier_name}/>
                        <BestCard icon={Award} label="En Yüksek Puan"
                                  value={`${Math.max(...suppliers.map(s=>s.rating)).toFixed(1)} ★`}
                                  sup={suppliers.find(s=>s.is_best_rating)?.supplier_name}/>
                    </div>

                    {/* Matrix */}
                    <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="comparison-matrix">
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="sticky left-0 z-20 bg-muted/70 min-w-[280px]">Kalem</th>
                                        {suppliers.map((s) => (
                                            <th key={s.quotation_id} className="text-right min-w-[180px]" data-testid={`quo-col-${s.supplier_name}`}>
                                                <div className="flex flex-col items-end gap-0.5">
                                                    <div className="text-foreground font-semibold text-[13px]">{s.supplier_name}</div>
                                                    <div className="flex items-center gap-1 text-[10px]">
                                                        {s.is_lowest_total && <span className="px-1 bg-emerald-100 text-emerald-700 rounded">EN DÜŞÜK</span>}
                                                        {s.is_fastest && <span className="px-1 bg-blue-100 text-blue-700 rounded">EN HIZLI</span>}
                                                        {s.is_best_rating && <span className="px-1 bg-amber-100 text-amber-700 rounded">EN YÜKSEK ★</span>}
                                                    </div>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {matrix.map((row) => (
                                        <tr key={row.product_id}>
                                            <td className="sticky left-0 z-10 bg-card border-r border-border">
                                                <div className="font-medium">{row.name}</div>
                                                <div className="text-xs text-muted-foreground font-mono">{row.sku} • {row.quantity} {row.unit}</div>
                                            </td>
                                            {row.cells.map((c, i) => (
                                                <td key={i} className={`text-right ${c.is_lowest ? "cell-best" : ""}`}>
                                                    {c.available ? (
                                                        <>
                                                            <div className="font-medium tabular-nums">
                                                                {fmtMoney(c.unit_price, c.currency)} <span className="text-xs text-muted-foreground">/ {row.unit}</span>
                                                            </div>
                                                            <div className="text-[11px] text-muted-foreground tabular-nums">
                                                                Satır: {fmtMoney(c.line_total, c.currency)}
                                                                {c.discount_pct > 0 && <> • -%{c.discount_pct}</>}
                                                            </div>
                                                            {c.is_lowest && <div className="text-[10px] font-medium text-emerald-700 mt-0.5">✓ EN DÜŞÜK</div>}
                                                        </>
                                                    ) : (
                                                        <div className="text-xs text-muted-foreground italic">—</div>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    {/* Summary rows */}
                                    <tr className="bg-muted/30">
                                        <td className="sticky left-0 z-10 bg-muted/60 border-r border-border font-medium">Ara Toplam</td>
                                        {suppliers.map(s => (
                                            <td key={s.quotation_id} className="text-right tabular-nums">{fmtMoney(s.subtotal, s.currency)}</td>
                                        ))}
                                    </tr>
                                    <tr className="bg-muted/30">
                                        <td className="sticky left-0 z-10 bg-muted/60 border-r border-border font-medium">İndirim</td>
                                        {suppliers.map(s => (
                                            <td key={s.quotation_id} className="text-right tabular-nums text-muted-foreground">-{fmtMoney(s.total_discount, s.currency)}</td>
                                        ))}
                                    </tr>
                                    <tr className="bg-muted/30">
                                        <td className="sticky left-0 z-10 bg-muted/60 border-r border-border font-medium">KDV</td>
                                        {suppliers.map(s => (
                                            <td key={s.quotation_id} className="text-right tabular-nums text-muted-foreground">+{fmtMoney(s.total_vat, s.currency)}</td>
                                        ))}
                                    </tr>
                                    <tr className="bg-muted/30">
                                        <td className="sticky left-0 z-10 bg-muted/60 border-r border-border font-medium">Nakliye</td>
                                        {suppliers.map(s => (
                                            <td key={s.quotation_id} className="text-right tabular-nums text-muted-foreground">+{fmtMoney(s.freight_cost, s.currency)}</td>
                                        ))}
                                    </tr>
                                    <tr className="bg-slate-900 dark:bg-slate-900 text-white">
                                        <td className="sticky left-0 z-10 bg-slate-900 border-r border-slate-800 font-semibold text-white">TOPLAM (Landed)</td>
                                        {suppliers.map(s => (
                                            <td key={s.quotation_id}
                                                data-testid={`landed-total-${s.supplier_name}`}
                                                className={`text-right font-bold tabular-nums ${s.is_lowest_total ? "text-emerald-300" : "text-white"}`}>
                                                {fmtMoney(s.total_amount, s.currency)}
                                                {s.currency !== "TRY" && (
                                                    <div className="text-[10px] font-normal text-white/70">≈ {fmtMoney(s.total_amount_base, "TRY")}</div>
                                                )}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Terms rows */}
                                    <SumRow label="Vade" suppliers={suppliers} render={(s)=>`${s.payment_term_days} gün`} bestKey="is_best_terms"/>
                                    <SumRow label="Teslim Süresi" suppliers={suppliers} render={(s)=>`${s.delivery_days} gün`} bestKey="is_fastest"/>
                                    <SumRow label="Garanti" suppliers={suppliers} render={(s)=>`${s.warranty_months} ay`}/>
                                    <SumRow label="Puan" suppliers={suppliers} render={(s)=>(
                                        <span className="inline-flex items-center gap-1">{s.rating.toFixed(1)}<Star size={11} className="fill-amber-500 text-amber-500"/></span>
                                    )} bestKey="is_best_rating"/>
                                    <SumRow label="Teslimat Başarısı" suppliers={suppliers} render={(s)=>`%${s.delivery_success_rate.toFixed(0)}`}/>
                                    <SumRow label="Kalite Skoru" suppliers={suppliers} render={(s)=>`${s.quality_score.toFixed(0)}/100`}/>

                                    <tr>
                                        <td className="sticky left-0 z-10 bg-card border-r border-border font-medium">Eylem</td>
                                        {suppliers.map(s => (
                                            <td key={s.quotation_id} className="text-right">
                                                {canCreatePo ? (
                                                <Button size="sm"
                                                        data-testid={`select-quo-${s.supplier_name}`}
                                                        onClick={()=>{ setSelectedQuo(s); setPoOpen(true); }}
                                                        variant={s.is_lowest_total ? "default" : "outline"}>
                                                    <ShoppingCart size={12} className="mr-1.5"/>
                                                    Bu Teklifi Seç → PO
                                                </Button>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">Yetkiniz yok</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Create PO dialog */}
            <Dialog open={poOpen} onOpenChange={setPoOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Sipariş Oluştur</DialogTitle></DialogHeader>
                    <PoFromQuo quotation={selectedQuo} projects={projects} pr={pr} onSubmit={createPO}/>
                </DialogContent>
            </Dialog>

            <div className="mt-4">
                <AttachmentsPanel entityType="rfq" entityId={id}/>
            </div>
        </AppShell>
    );
}

function BestCard({ icon: Icon, label, value, sup }) {
    return (
        <div className="bg-card border border-border rounded-md p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                <Icon size={14}/> {label}
            </div>
            <div className="text-lg font-semibold mt-2 tabular-nums">{value}</div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">{sup || "—"}</div>
        </div>
    );
}

function SumRow({ label, suppliers, render, bestKey }) {
    return (
        <tr>
            <td className="sticky left-0 z-10 bg-card border-r border-border font-medium text-xs">{label}</td>
            {suppliers.map(s => (
                <td key={s.quotation_id} className={`text-right text-sm ${bestKey && s[bestKey] ? "cell-best font-medium" : ""}`}>
                    {render(s)}
                </td>
            ))}
        </tr>
    );
}

function QuotationForm({ rfq, pr, onClose, onSaved }) {
    const [suppliers, setSuppliers] = useState([]);
    const [supplierId, setSupplierId] = useState("");
    const [currency, setCurrency] = useState("TRY");
    const [paymentTerm, setPaymentTerm] = useState(30);
    const [deliveryDays, setDeliveryDays] = useState(7);
    const [freight, setFreight] = useState(0);
    const [warranty, setWarranty] = useState(12);
    const [lines, setLines] = useState([]);

    useEffect(() => {
        if (!rfq) return;
        api.get("/suppliers").then((r) => {
            const filtered = r.data.filter(s => rfq.supplier_ids.includes(s.id));
            setSuppliers(filtered);
        });
        setLines((pr?.lines || []).map(l => ({
            product_id: l.product_id, sku: l.sku, name: l.name, unit: l.unit,
            quantity: l.quantity, unit_price: 0, discount_pct: 0, vat_pct: 20,
        })));
    }, [rfq, pr]);

    const updLine = (i, f, v) => {
        const nl = [...lines]; nl[i] = { ...nl[i], [f]: v }; setLines(nl);
    };

    const submit = async () => {
        if (!supplierId) return toast.error("Tedarikçi seçin");
        if (lines.some(l => !l.unit_price || Number(l.unit_price) <= 0)) return toast.error("Tüm kalemler için birim fiyat girin");
        try {
            const payload = {
                rfq_id: rfq.id, supplier_id: supplierId,
                currency, exchange_rate: currency === "TRY" ? 1 : (currency === "USD" ? 33 : currency === "EUR" ? 36 : 1),
                payment_term_days: Number(paymentTerm), delivery_days: Number(deliveryDays),
                freight_cost: Number(freight), warranty_months: Number(warranty),
                lines: lines.map(l => ({
                    product_id: l.product_id, sku: l.sku, name: l.name, unit: l.unit,
                    quantity: Number(l.quantity), unit_price: Number(l.unit_price),
                    discount_pct: Number(l.discount_pct || 0), vat_pct: Number(l.vat_pct || 20),
                })),
            };
            await api.post("/quotations", payload);
            toast.success("Teklif eklendi");
            onClose(); onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Tedarikçi Teklifi Ekle</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-4">
                <F label="Tedarikçi *">
                    <select data-testid="quo-supplier" className="h-10 border border-input rounded-md px-3 bg-background text-sm" value={supplierId} onChange={(e)=>setSupplierId(e.target.value)}>
                        <option value="">Seç...</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                    </select>
                </F>
                <F label="Para Birimi">
                    <select className="h-10 border border-input rounded-md px-3 bg-background text-sm" value={currency} onChange={(e)=>setCurrency(e.target.value)}>
                        <option>TRY</option><option>USD</option><option>EUR</option>
                    </select>
                </F>
                <F label="Vade (gün)"><Input type="number" value={paymentTerm} onChange={(e)=>setPaymentTerm(e.target.value)}/></F>
                <F label="Teslim Süresi (gün)"><Input type="number" value={deliveryDays} onChange={(e)=>setDeliveryDays(e.target.value)}/></F>
                <F label="Nakliye"><Input type="number" value={freight} onChange={(e)=>setFreight(e.target.value)}/></F>
                <F label="Garanti (ay)"><Input type="number" value={warranty} onChange={(e)=>setWarranty(e.target.value)}/></F>
            </div>

            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Kalem Fiyatları</div>
                <div className="border border-border rounded-md overflow-hidden">
                    <table className="data-table">
                        <thead>
                            <tr><th>Ürün</th><th className="text-right">Miktar</th><th className="text-right">Birim Fiyat *</th><th className="text-right">İnd. %</th><th className="text-right">KDV %</th></tr>
                        </thead>
                        <tbody>
                            {lines.map((l, i) => (
                                <tr key={i}>
                                    <td>
                                        <div className="text-xs font-mono">{l.sku}</div>
                                        <div className="text-sm">{l.name}</div>
                                    </td>
                                    <td className="text-right tabular-nums">{l.quantity} {l.unit}</td>
                                    <td><Input data-testid={`quo-line-price-${i}`} type="number" step="0.01" className="h-9 text-right" value={l.unit_price} onChange={(e)=>updLine(i, "unit_price", e.target.value)}/></td>
                                    <td><Input type="number" className="h-9 text-right" value={l.discount_pct} onChange={(e)=>updLine(i, "discount_pct", e.target.value)}/></td>
                                    <td><Input type="number" className="h-9 text-right" value={l.vat_pct} onChange={(e)=>updLine(i, "vat_pct", e.target.value)}/></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>İptal</Button>
                <Button data-testid="save-quo" onClick={submit}>Teklifi Kaydet</Button>
            </DialogFooter>
        </DialogContent>
    );
}

function PoFromQuo({ quotation, projects, pr, onSubmit }) {
    // Prefill from source PR:
    //   project_id: undefined/"" on PR → Genel Gider / Projesiz
    //   required_date / delivery_location carry over to PO delivery fields
    const [projectId, setProjectId] = useState(pr?.project_id || "");
    const [deliveryDate, setDeliveryDate] = useState(pr?.required_date ? String(pr.required_date).slice(0, 10) : "");
    const [deliveryAddress, setDeliveryAddress] = useState(pr?.delivery_location || "");

    // Keep prefill in sync if PR loads after the modal (defensive; parent already gates on quotation).
    useEffect(() => {
        setProjectId(pr?.project_id || "");
        setDeliveryDate(pr?.required_date ? String(pr.required_date).slice(0, 10) : "");
        setDeliveryAddress(pr?.delivery_location || "");
    }, [pr?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!quotation) return null;
    const activeProjects = (projects || []).filter(p => p.status === "active" || !p.status);
    return (
        <>
            <div className="text-sm space-y-1 border border-border rounded-md p-3 mb-3 bg-muted/30">
                <div><b>Tedarikçi:</b> {quotation.supplier_name}</div>
                <div><b>Toplam:</b> {fmtMoney(quotation.total_amount, quotation.currency)}</div>
                <div><b>Vade:</b> {quotation.payment_term_days} gün</div>
                <div className="text-xs text-muted-foreground pt-1">Kaynak talep: <b className="text-foreground font-mono">{pr?.number || "—"}</b></div>
            </div>
            <div className="space-y-3">
                <F label="Proje">
                    <select
                        data-testid="po-project"
                        className="h-10 border border-input rounded-md px-3 bg-background text-sm w-full"
                        value={projectId}
                        onChange={(e)=>setProjectId(e.target.value)}
                    >
                        <option value="">Genel Gider / Projesiz</option>
                        {activeProjects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ""}{p.name}</option>)}
                    </select>
                </F>
                <F label="Teslim Tarihi">
                    <Input
                        data-testid="po-delivery-date"
                        type="date"
                        value={deliveryDate}
                        onChange={(e)=>setDeliveryDate(e.target.value)}
                    />
                </F>
                <F label="Teslim Adresi">
                    <Input
                        data-testid="po-delivery-address"
                        value={deliveryAddress}
                        onChange={(e)=>setDeliveryAddress(e.target.value)}
                        placeholder="Merkez Depo, Şantiye A, vb."
                    />
                </F>
            </div>
            <DialogFooter className="mt-4">
                <Button data-testid="confirm-po" onClick={()=>onSubmit({
                    project_id: projectId || null,
                    delivery_date: deliveryDate || null,
                    delivery_address: deliveryAddress || null,
                })}>Siparişi Oluştur</Button>
            </DialogFooter>
        </>
    );
}

function F({label,children}){return<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>}
